import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellRing, CheckCircle2, AlertTriangle, Send } from "lucide-react";

export const PushNotificationCard = () => {
  const { token } = useAuth();
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };
  const permission = typeof Notification !== "undefined" ? Notification.permission : "unsupported";

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/push/tokens`, { headers });
      setDevices(data.count);
    } catch { setDevices(0); }
  }, [token]);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const enablePush = async () => {
    setBusy(true);
    setLastError(null);
    try {
      const { requestPushPermission } = await import("@/lib/pushNotifications");
      const pushToken = await requestPushPermission();
      if (!pushToken) {
        const perm = Notification.permission;
        const msg = perm === "denied"
          ? "Notifications are blocked for this site. Click the padlock icon in the address bar → Site settings → Notifications → Allow, then try again."
          : "Could not create a push token. Please try again or use Chrome/Edge on desktop.";
        setLastError(msg);
        toast.error(msg);
        return;
      }
      const res = await axios.post(`${API}/push/register`, { token: pushToken, platform: "web" }, { headers });
      if (res.data?.status === "registered") {
        toast.success("This device is now registered for notifications!");
        fetchDevices();
      }
    } catch (err) {
      const msg = err?.message || "Something went wrong enabling notifications";
      setLastError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/push/test`, {}, { headers });
      if (data.status === "sent") toast.success(`Test notification sent to ${data.devices} device(s) — check your screen!`);
      else toast.warning(data.message || "Could not send test");
    } catch { toast.error("Test send failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="push-notification-card" className="card-omni p-8">
      <h2 className="font-heading text-xl font-medium mb-1 flex items-center gap-2">
        <BellRing className="w-5 h-5 text-amber-500" strokeWidth={1.5} /> Push Notifications
      </h2>
      <p className="text-sm text-muted-foreground mb-4">Get gentle check-in nudges and important updates on this device.</p>

      <div className="flex items-center gap-4 text-xs mb-4">
        <span className="flex items-center gap-1.5" data-testid="push-permission-state">
          {permission === "granted"
            ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Browser permission: granted</>
            : permission === "denied"
              ? <><AlertTriangle className="w-3.5 h-3.5 text-red-500" /> Browser permission: blocked</>
              : <><Bell className="w-3.5 h-3.5 text-muted-foreground" /> Browser permission: not asked yet</>}
        </span>
        <span data-testid="push-device-count" className="text-muted-foreground">
          {devices === null ? "…" : `${devices} device(s) registered`}
        </span>
      </div>

      {permission === "denied" && (
        <p className="text-xs text-red-500 mb-3" data-testid="push-blocked-help">
          Notifications are blocked for this site. Click the padlock 🔒 in the address bar → Site settings → Notifications → <strong>Allow</strong>, reload, then press Enable.
        </p>
      )}
      {lastError && <p className="text-xs text-amber-600 mb-3" data-testid="push-last-error">{lastError}</p>}

      <div className="flex gap-3">
        <button
          data-testid="enable-push-btn"
          onClick={enablePush}
          disabled={busy}
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {devices > 0 ? "Re-enable on this device" : "Enable notifications"}
        </button>
        <button
          data-testid="test-push-btn"
          onClick={sendTest}
          disabled={busy || !devices}
          className="px-5 py-2.5 rounded-full border border-border text-sm font-medium flex items-center gap-1.5 hover:bg-muted transition-colors disabled:opacity-50"
        >
          <Send className="w-3.5 h-3.5" /> Send test notification
        </button>
      </div>
    </div>
  );
};
